import { Inject, Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable, Subscription, firstValueFrom } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';

import { ThemeMode } from '../types/theme-mode.type';
import { SideBarMode } from '../types/sidebar-mode.type';

import { AuthService, UsersService } from '@itb-monorepo/services';
import { APP_TOKEN, AppsEnum } from '@itb-monorepo/enums';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  defaultMode: ThemeMode = 'light';
  themeMode?: ThemeMode;

  subscriptions: Subscription[] = [];

  private themeMode$: BehaviorSubject<ThemeMode>;
  private sideBarMode$: BehaviorSubject<SideBarMode>;

  public app = inject(APP_TOKEN);

  private usersService = inject(UsersService);
  private authService = inject(AuthService);
  private cookieService = inject(CookieService);

  constructor(@Inject(DOCUMENT) private doc: Document) {
    this.themeMode$ = new BehaviorSubject<ThemeMode>(this.defaultMode);
    this.sideBarMode$ = new BehaviorSubject<SideBarMode>('closed');

    const cookieTheme = this.cookieService.get('themeMode');

    if (cookieTheme === 'dark' || cookieTheme === 'light') {
      this.themeMode = cookieTheme as ThemeMode;
    } else {
      this.themeMode = this.defaultMode;
    }

    this.themeMode$.next(this.themeMode);
    document.documentElement.setAttribute('data-bs-theme', this.themeMode);
  }

  toggleThemeMode(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';

    this.changeTheme(this.themeMode);

    let domain = this.doc.location.hostname;

    if (domain.includes('itbfx')) {
      const domainSplit = domain.split('.');

      if (domainSplit.length > 2) {
        domain = domainSplit.slice(-2).join('.');
      }
    }

    this.cookieService.set(
      'themeMode',
      this.themeMode,
      undefined,
      '/',
      `.${domain}`
    );

    this.themeMode$.next(this.themeMode);
    document.documentElement.setAttribute('data-bs-theme', this.themeMode);
  }

  getSideBarState(): Observable<SideBarMode> {
    return this.sideBarMode$.asObservable();
  }

  showSideBar(): void {
    this.sideBarMode$.next('opened');
  }

  hideSideBar(): void {
    this.sideBarMode$.next('closed');
  }

  minimizeSideBar(): void {
    this.sideBarMode$.next('minimized');
  }

  getThemeMode(): Observable<ThemeMode> {
    return this.themeMode$.asObservable();
  }

  async changeTheme(theme: ThemeMode): Promise<void> {
    if (!this.authService.isAuthenticated || this.app === AppsEnum.SUPPORTER) {
      return;
    }

    try {
      const currentUser = await firstValueFrom(
        this.usersService.loadCurrentUser()
      );

      const updatedUser = {
        ...currentUser,
        theme: theme.toUpperCase(),
      };

      await firstValueFrom(
        this.usersService.modifyCurrentUserProfile(updatedUser)
      );
    } catch (error) {
      console.error('Error while updating user theme:', error);
    }
  }
}
